import { supabase } from '../lib/supabase';
import { Result } from './serpApi'; // Reutilizando a interface Result como base para Leads
import { getCountryNameFromCode, normalizePhoneNumber } from '../utils/ddiUtils'; // Import DDI utilities

// Definindo a interface Lead salva (baseada na tabela leads)
export interface SavedLead {
  id: string;
  company_name: string;
  website: string | null;
  phone_number: string | null;
  email: string | null;
  address: string | null;
  location: string | null; // Country name
  category: string | null;
  whatsapp_status: 'Validado' | 'Pendente' | 'Inválido';
  rating: number;
  prospected: boolean;
  external_id: string | null;
  created_at: string;
}

// Mapeia os dados do formulário/busca para o formato do banco de dados
export interface LeadInsertData {
  company_name: string;
  website?: string;
  phone_number?: string;
  email?: string;
  address?: string;
  location?: string; // Country name
  category?: string;
  whatsapp_status?: 'Validado' | 'Pendente' | 'Inválido';
  rating?: number;
  prospected?: boolean;
  external_id?: string;
}

/**
 * Busca todos os leads salvos pelo usuário autenticado.
 */
export async function fetchLeads(): Promise<SavedLead[]> {
  const { data, error } = await supabase
    .from('leads')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error fetching leads:', error);
    }
    throw new Error('Falha ao carregar leads salvos.');
  }

  return data as SavedLead[];
}

/**
 * Salva um novo lead.
 */
export async function saveLead(leadData: LeadInsertData): Promise<SavedLead> {
  // 1. Obter o ID do usuário logado
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    throw new Error('Usuário não autenticado. Por favor, faça login novamente.');
  }

  const dataToInsert = {
    ...leadData,
    user_id: user.id, // Adicionando user_id explicitamente para satisfazer o RLS
  };

  const { data, error } = await supabase
    .from('leads')
    .insert([dataToInsert])
    .select()
    .single();

  if (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error saving lead:', error);
    }
    // Se o erro for de duplicidade (23505), retornamos uma mensagem amigável
    if (error.code === '23505') {
      throw new Error('Este lead já foi salvo anteriormente.');
    }
    throw new Error('Falha ao salvar o lead.');
  }

  return data as SavedLead;
}

/**
 * Atualiza o status de um lead salvo.
 */
export async function updateLeadStatus(id: string, status: 'Validado' | 'Pendente' | 'Inválido', prospected?: boolean): Promise<SavedLead> {
  const updateData: any = { whatsapp_status: status };
  if (prospected !== undefined) {
    updateData.prospected = prospected;
  }

  const { data, error } = await supabase
    .from('leads')
    .update(updateData)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error updating lead status:', error);
    }
    throw new Error('Falha ao atualizar o status do lead.');
  }

  return data as SavedLead;
}

/**
 * Exclui leads por ID.
 */
export async function deleteLeads(ids: string[]): Promise<void> {
  // O RLS garante que o usuário só pode deletar seus próprios leads.
  const { error } = await supabase
    .from('leads')
    .delete()
    .in('id', ids);

  if (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error deleting leads:', error);
    }
    throw new Error('Falha ao excluir leads.');
  }
}

/**
 * Converte um Result de busca em LeadInsertData para salvar.
 */
export function mapResultToLeadInsert(result: Result, countryCode?: string): LeadInsertData {
  // Get country name from code if provided
  let location: string | undefined;
  if (countryCode) {
    const countryName = getCountryNameFromCode(countryCode);
    location = countryName || undefined;
  }

  // Normalize phone number with DDI and remove + for storage
  let phoneNumber = result.phone;
  if (phoneNumber && countryCode) {
    const normalized = normalizePhoneNumber(phoneNumber, countryCode);
    if (normalized) {
      // Remove + sign for consistent storage
      phoneNumber = normalized.replace(/^\+/, '');
    }
  }

  return {
    company_name: result.empresa,
    website: result.website,
    phone_number: phoneNumber,
    email: undefined,
    address: result.localizacao,
    location, // Add country name
    category: undefined,
    whatsapp_status: result.status,
    rating: Math.round(result.rating || 0), // ARREDONDANDO O RATING AQUI
    prospected: result.prospected,
    external_id: result.place_id,
  };
}

/**
 * Atualiza campos específicos de um lead.
 */
export async function updateLead(id: string, updates: Partial<LeadInsertData>): Promise<SavedLead> {
  const { data, error } = await supabase
    .from('leads')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error updating lead:', error);
    }
    throw new Error('Falha ao atualizar o lead.');
  }

  return data as SavedLead;
}